// ============================================================================
// PLACEMENT.JS — Crafting placement flow for Palm Island (pi-tx9.4 + pi-tx9.5)
//
// Handles the full craft-to-place pipeline:
//   1. CRAFT_STARTED event received -> deduct resources
//   2. Enter placement mode with semi-transparent preview mesh
//   3. Preview follows cursor via terrain raycast
//   4. Valid/invalid coloring based on placement rules
//   5. Scroll wheel rotates, Escape cancels (refunds), click confirms
//   6. On confirm: persist to state, emit events, clean up
// ============================================================================

import * as THREE from 'three';
import { eventBus, GameEvents } from './events.js';
import { getRecipe, RECIPE_REGISTRY } from './crafting.js';
import { spendResources, addToInventory } from './resources.js';
import { createCraftableMesh } from '../meshes/craftables.js';
import { getGameState, saveGameState } from './state.js';
import { PLACEMENT } from './balance.js';
import { hasUpgrade, UPGRADE_DEFS } from './upgrades.js';

// ---------------------------------------------------------------------------
// Placement state
// ---------------------------------------------------------------------------
let isPlacementMode = false;
let currentRecipeId = null;
let previewMesh = null;
let previewValid = false;
let rotationY = 0;

// Scene references (set once via initPlacement)
let scene = null;
let camera = null;
let renderer = null;
let sandMesh = null;

// Raycaster for terrain intersection
const placementRaycaster = new THREE.Raycaster();

// Reusable vector for NDC calculations
const _pointer = new THREE.Vector2();

// Bound listener references (for cleanup)
let _onPointerMove = null;
let _onClick = null;
let _onKeyDown = null;
let _onWheel = null;

// Colors for valid/invalid tinting
const VALID_COLOR = new THREE.Color(0x44ff44);
const INVALID_COLOR = new THREE.Color(0xff4444);
const PREVIEW_OPACITY = 0.5;

// Y offset so preview doesn't z-fight with terrain
const PREVIEW_Y_OFFSET = 0.05;

// Minimum sand height to be considered valid terrain
const MIN_SAND_HEIGHT = 0.3;

// Maximum sand height for water-edge placement
const MAX_WATER_EDGE_HEIGHT = 1.0;

// Maximum distance between two trees for hammock placement
const MAX_TREE_GAP = 8;

// Maximum distance from midpoint of two trees for hammock placement
const MAX_MIDPOINT_DIST = 2;

// Maximum distance from any tree for near_tree placement
const MAX_NEAR_TREE_DIST = 3;

// ---------------------------------------------------------------------------
// Palm tree positions (populated externally via setPalmTreePositions)
// ---------------------------------------------------------------------------
let palmTreePositions = [];

/**
 * Set the array of palm tree world positions for placement constraint checks.
 * Called from main.js after palm trees are created.
 * @param {THREE.Vector3[]} positions
 */
export function setPalmTreePositions(positions) {
  palmTreePositions = positions;
  console.log(`[Placement] Registered ${positions.length} palm tree positions`);
}

// ---------------------------------------------------------------------------
// ID generator
// ---------------------------------------------------------------------------
function generateId() {
  return 'obj_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

// ---------------------------------------------------------------------------
// initPlacement — called once from main.js after scene is set up
// ---------------------------------------------------------------------------
export function initPlacement(sceneRef, cameraRef, rendererRef, sandMeshRef) {
  scene = sceneRef;
  camera = cameraRef;
  renderer = rendererRef;
  sandMesh = sandMeshRef;

  // Wire up the CRAFT_STARTED event
  eventBus.on(GameEvents.CRAFT_STARTED, (data) => {
    startPlacement(data.recipeId);
  });

  // Restore any previously placed objects from save state
  restorePlacedObjects();

  console.log('[Placement] Initialized');
}

export function updatePlacementSandMesh(newSandMesh) {
  sandMesh = newSandMesh;
}

// ---------------------------------------------------------------------------
// restorePlacedObjects — recreate meshes from saved state on game load
// ---------------------------------------------------------------------------
export function restorePlacedObjects() {
  const state = getGameState();
  if (!state.placedObjects || state.placedObjects.length === 0) return;

  const validObjects = [];

  for (const saved of state.placedObjects) {
    const recipe = getRecipe(saved.recipeId);
    if (!recipe) {
      console.warn(`[Placement] Unknown recipe "${saved.recipeId}", removing from save`);
      continue;
    }

    const mesh = createCraftableMesh(saved.recipeId);
    if (!mesh) continue;

    mesh.position.set(saved.position.x, saved.position.y, saved.position.z);
    if (saved.rotation) {
      mesh.rotation.set(saved.rotation.x, saved.rotation.y, saved.rotation.z);
    }

    mesh.userData.isPlacedObject = true;
    mesh.userData.placedObjectId = saved.id;
    mesh.userData.recipeId = saved.recipeId;

    scene.add(mesh);

    // Run onPlace callback to restore lights, particles, etc.
    if (recipe.onPlace) {
      try {
        recipe.onPlace(mesh);
      } catch (e) {
        console.error(`[Placement] onPlace callback failed for ${saved.recipeId}:`, e);
      }
    }

    // Track sand castle erosion on restore (pi-t0j.3)
    if (saved.recipeId === 'sand_castle') {
      trackSandCastleErosion(mesh, saved.timestamp, saved.id);
      // If the sand castle was fully eroded, it was already removed by trackSandCastleErosion
      if (!sandCastleErosion.has(mesh)) {
        continue; // Don't add to validObjects — it's been removed
      }
    }

    validObjects.push(saved);
  }

  // Update state if any invalid objects were removed
  if (validObjects.length !== state.placedObjects.length) {
    state.placedObjects = validObjects;
    saveGameState(state);
  }

  console.log(`[Placement] Restored ${validObjects.length} placed objects`);
}

// ---------------------------------------------------------------------------
// startPlacement — enter placement mode for a given recipe
// ---------------------------------------------------------------------------
function startPlacement(recipeId) {
  // Prevent re-entry
  if (isPlacementMode) {
    console.warn('[Placement] Already in placement mode, ignoring');
    return;
  }

  const recipe = getRecipe(recipeId);
  if (!recipe) {
    console.error(`[Placement] Unknown recipe: ${recipeId}`);
    return;
  }

  // Deduct resources (atomic: all or nothing)
  const spent = spendResources(recipe.cost);
  if (!spent) {
    console.warn(`[Placement] Cannot afford recipe: ${recipeId}`);
    return;
  }

  // Create preview mesh
  previewMesh = createCraftableMesh(recipeId);
  if (!previewMesh) {
    // Refund if mesh creation fails
    refundResources(recipe.cost);
    console.error(`[Placement] Failed to create mesh for: ${recipeId}`);
    return;
  }

  // Make preview semi-transparent with initial valid tint
  setPreviewTransparency(previewMesh, PREVIEW_OPACITY);
  previewValid = false;
  rotationY = 0;

  // Position off-screen initially (will be moved on first pointermove)
  previewMesh.position.set(0, -100, 0);

  scene.add(previewMesh);
  currentRecipeId = recipeId;
  isPlacementMode = true;

  eventBus.emit(GameEvents.PLACEMENT_STARTED, { recipeId });

  // Attach listeners
  _onPointerMove = (e) => updatePlacementPreview(e);
  _onClick = (e) => onPlacementClick(e);
  _onKeyDown = (e) => onPlacementKeyDown(e);
  _onWheel = (e) => rotatePlacement(e);

  const canvas = renderer.domElement;
  canvas.addEventListener('pointermove', _onPointerMove);
  canvas.addEventListener('click', _onClick);
  document.addEventListener('keydown', _onKeyDown);
  canvas.addEventListener('wheel', _onWheel, { passive: true });

  console.log(`[Placement] Started placement for: ${recipeId}`);
}

// ---------------------------------------------------------------------------
// updatePlacementPreview — move preview to cursor position on terrain
// ---------------------------------------------------------------------------
function updatePlacementPreview(event) {
  if (!isPlacementMode || !previewMesh || !sandMesh) return;

  // Compute NDC from pointer event
  const rect = renderer.domElement.getBoundingClientRect();
  _pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  _pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  // Raycast against the sand mesh
  placementRaycaster.setFromCamera(_pointer, camera);
  const intersects = placementRaycaster.intersectObject(sandMesh, true);

  if (intersects.length > 0) {
    const hit = intersects[0];
    previewMesh.position.copy(hit.point);
    previewMesh.position.y += PREVIEW_Y_OFFSET;
    previewMesh.rotation.y = rotationY;

    // Validate placement
    const valid = checkPlacementValidity(hit.point);
    if (valid !== previewValid) {
      previewValid = valid;
      tintPreview(previewMesh, previewValid ? VALID_COLOR : INVALID_COLOR);
    }
  }
}

// ---------------------------------------------------------------------------
// getObjectLimit — dynamic limit accounting for upgrades
// ---------------------------------------------------------------------------
function getObjectLimit() {
  let limit = PLACEMENT.BASE_OBJECT_LIMIT; // 10
  if (hasUpgrade('island_expansion_1')) {
    limit += UPGRADE_DEFS.island_expansion_1.effects.additionalObjectSlots; // +5
  }
  return limit;
}

// ---------------------------------------------------------------------------
// checkPlacementValidity — determine if position is a valid placement spot
// ---------------------------------------------------------------------------
function checkPlacementValidity(point) {
  // 1. Get the active recipe's placement rules
  const recipe = getRecipe(currentRecipeId);
  const surface = recipe?.placementRules?.surface;

  // 2. Surface-specific height checks
  if (surface === 'water_edge') {
    // Water edge items must be in the low-terrain band near (but not in) water
    if (point.y < MIN_SAND_HEIGHT || point.y > MAX_WATER_EDGE_HEIGHT) {
      return false;
    }
  } else {
    // All other items must be on sand (above minimum height)
    if (point.y < MIN_SAND_HEIGHT) {
      return false;
    }
  }

  // 3. Surface-specific constraint checks
  if (surface === 'between_trees') {
    if (!checkBetweenTrees(point)) return false;
  }
  if (surface === 'near_tree') {
    if (!checkNearTree(point, MAX_NEAR_TREE_DIST)) return false;
  }

  // 4. Check placed object limit
  const state = getGameState();
  const placedObjects = state.placedObjects || [];
  if (placedObjects.length >= getObjectLimit()) {
    return false;
  }

  // 5. Check minimum spacing from other placed objects
  for (const obj of placedObjects) {
    const dx = point.x - obj.position.x;
    const dz = point.z - obj.position.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    if (dist < PLACEMENT.MIN_SPACING) {
      return false;
    }
  }

  // 6. Check distance from scene-placed meshes tagged as placed objects
  //    (handles objects placed in this session that may not yet be in state)
  if (scene) {
    for (const child of scene.children) {
      if (child.userData && child.userData.isPlacedObject && child !== previewMesh) {
        const dx = point.x - child.position.x;
        const dz = point.z - child.position.z;
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist < PLACEMENT.MIN_SPACING) {
          return false;
        }
      }
    }
  }

  return true;
}

// ---------------------------------------------------------------------------
// checkBetweenTrees — validate that point is between 2 nearby palm trees
// ---------------------------------------------------------------------------
function checkBetweenTrees(point) {
  if (palmTreePositions.length < 2) return false;

  // Find the 2 nearest trees to the placement point
  const sorted = palmTreePositions
    .map(p => ({ pos: p, dist: point.distanceTo(p) }))
    .sort((a, b) => a.dist - b.dist);

  const tree1 = sorted[0];
  const tree2 = sorted[1];

  // The two trees must be less than MAX_TREE_GAP units apart
  if (tree1.pos.distanceTo(tree2.pos) > MAX_TREE_GAP) return false;

  // The placement point must be within MAX_MIDPOINT_DIST of the midpoint
  const mid = tree1.pos.clone().add(tree2.pos).multiplyScalar(0.5);
  if (point.distanceTo(mid) > MAX_MIDPOINT_DIST) return false;

  return true;
}

// ---------------------------------------------------------------------------
// checkNearTree — validate that point is within maxDist of any palm tree
// ---------------------------------------------------------------------------
function checkNearTree(point, maxDist) {
  return palmTreePositions.some(p => point.distanceTo(p) < maxDist);
}

// ---------------------------------------------------------------------------
// onPlacementClick — confirm placement if valid
// ---------------------------------------------------------------------------
function onPlacementClick(event) {
  if (!isPlacementMode || !previewMesh) return;

  // Ignore clicks that don't hit the terrain (preview is at y=-100)
  if (previewMesh.position.y < -50) return;

  if (!previewValid) {
    console.log('[Placement] Invalid position, cannot place here');
    return;
  }

  confirmPlacement();
}

// ---------------------------------------------------------------------------
// confirmPlacement — finalize the placed object
// ---------------------------------------------------------------------------
function confirmPlacement() {
  const recipe = getRecipe(currentRecipeId);
  if (!recipe) return;

  // Create the final (non-transparent) mesh
  const finalMesh = createCraftableMesh(currentRecipeId);
  if (!finalMesh) {
    console.error('[Placement] Failed to create final mesh');
    return;
  }

  // Position and rotate to match preview
  finalMesh.position.copy(previewMesh.position);
  finalMesh.rotation.y = rotationY;

  // Generate a unique id for this placed object
  const objectId = generateId();

  // Tag as placed object for spacing checks and undo lookup
  finalMesh.userData.isPlacedObject = true;
  finalMesh.userData.recipeId = currentRecipeId;
  finalMesh.userData.placedObjectId = objectId;

  scene.add(finalMesh);

  // Run onPlace callback if registered
  if (recipe.onPlace && typeof recipe.onPlace === 'function') {
    try {
      recipe.onPlace(finalMesh);
    } catch (err) {
      console.error('[Placement] onPlace callback error:', err);
    }
  }

  // Persist to game state
  const placedObj = {
    id: objectId,
    recipeId: currentRecipeId,
    recipeVersion: recipe.recipeVersion || 1,
    position: {
      x: finalMesh.position.x,
      y: finalMesh.position.y,
      z: finalMesh.position.z,
    },
    rotation: {
      x: finalMesh.rotation.x,
      y: finalMesh.rotation.y,
      z: finalMesh.rotation.z,
    },
    timestamp: Date.now(),
  };

  const state = getGameState();
  if (!state.placedObjects) state.placedObjects = [];
  state.placedObjects.push(placedObj);
  saveGameState(state);

  // Emit events (mesh included for undo support)
  eventBus.emit(GameEvents.OBJECT_PLACED, {
    id: placedObj.id,
    recipeId: currentRecipeId,
    position: placedObj.position,
    mesh: finalMesh,
  });
  eventBus.emit(GameEvents.CRAFT_COMPLETED, {
    recipeId: currentRecipeId,
  });

  // Track sand castle erosion (pi-t0j.3)
  if (currentRecipeId === 'sand_castle') {
    trackSandCastleErosion(finalMesh, placedObj.timestamp, placedObj.id);
  }

  // Play build animation — pieces fly in and assemble over 2 seconds
  playBuildAnimation(finalMesh, () => {
    console.log(`[Placement] Build animation complete for ${placedObj.recipeId}`);
    eventBus.emit(GameEvents.SPAWN_PARTICLES, {
      position: placedObj.position,
      type: 'sparkle',
      count: 12,
    });
  });

  console.log(`[Placement] Placed ${currentRecipeId} at`, placedObj.position);

  // Clean up
  cleanupPlacement();
}

// ---------------------------------------------------------------------------
// cancelPlacement — abort and refund resources
// ---------------------------------------------------------------------------
function cancelPlacement() {
  if (!isPlacementMode) return;

  const recipe = getRecipe(currentRecipeId);
  if (recipe) {
    refundResources(recipe.cost);
  }

  eventBus.emit(GameEvents.PLACEMENT_CANCELLED, {
    recipeId: currentRecipeId,
  });

  console.log(`[Placement] Cancelled placement for: ${currentRecipeId}`);
  cleanupPlacement();
}

// ---------------------------------------------------------------------------
// onPlacementKeyDown — handle Escape to cancel
// ---------------------------------------------------------------------------
function onPlacementKeyDown(event) {
  if (event.key === 'Escape' && isPlacementMode) {
    event.preventDefault();
    event.stopPropagation();
    cancelPlacement();
  }
}

// ---------------------------------------------------------------------------
// rotatePlacement — rotate preview on scroll wheel
// ---------------------------------------------------------------------------
function rotatePlacement(event) {
  if (!isPlacementMode || !previewMesh) return;

  // Rotate by 15 degrees per wheel tick
  const step = Math.PI / 12;
  rotationY += event.deltaY > 0 ? step : -step;
  previewMesh.rotation.y = rotationY;
}

// ---------------------------------------------------------------------------
// cleanupPlacement — remove preview and listeners, reset state
// ---------------------------------------------------------------------------
function cleanupPlacement() {
  // Remove preview mesh from scene
  if (previewMesh && scene) {
    scene.remove(previewMesh);

    // Dispose geometry and materials
    previewMesh.traverse((child) => {
      if (child.isMesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });
  }

  // Remove listeners
  const canvas = renderer ? renderer.domElement : null;
  if (canvas && _onPointerMove) {
    canvas.removeEventListener('pointermove', _onPointerMove);
  }
  if (canvas && _onClick) {
    canvas.removeEventListener('click', _onClick);
  }
  if (_onKeyDown) {
    document.removeEventListener('keydown', _onKeyDown);
  }
  if (canvas && _onWheel) {
    canvas.removeEventListener('wheel', _onWheel);
  }

  // Reset state
  previewMesh = null;
  currentRecipeId = null;
  isPlacementMode = false;
  previewValid = false;
  rotationY = 0;
  _onPointerMove = null;
  _onClick = null;
  _onKeyDown = null;
  _onWheel = null;
}

// ---------------------------------------------------------------------------
// Helpers: transparency and tinting
// ---------------------------------------------------------------------------

/**
 * Make all materials in a mesh group semi-transparent.
 */
function setPreviewTransparency(group, opacity) {
  group.traverse((child) => {
    if (child.isMesh && child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const mat of mats) {
        mat.transparent = true;
        mat.opacity = opacity;
        mat.depthWrite = false; // prevent z-fighting artifacts
      }
    }
  });
}

/**
 * Apply a color tint to all materials in a mesh group.
 * Multiplies the existing color with the tint color.
 */
function tintPreview(group, tintColor) {
  group.traverse((child) => {
    if (child.isMesh && child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const mat of mats) {
        // Store original color on first tint
        if (!mat.userData) mat.userData = {};
        if (!mat.userData._originalColor) {
          mat.userData._originalColor = mat.color.clone();
        }
        // Apply tint by lerping original toward tint color
        mat.color.copy(mat.userData._originalColor).lerp(tintColor, 0.4);
      }
    }
  });
}

// ---------------------------------------------------------------------------
// Build animation — pieces fly in and assemble with bounce easing
// ---------------------------------------------------------------------------

/**
 * easeOutBounce easing function.
 */
function easeOutBounce(t) {
  if (t < 1 / 2.75) {
    return 7.5625 * t * t;
  } else if (t < 2 / 2.75) {
    t -= 1.5 / 2.75;
    return 7.5625 * t * t + 0.75;
  } else if (t < 2.5 / 2.75) {
    t -= 2.25 / 2.75;
    return 7.5625 * t * t + 0.9375;
  } else {
    t -= 2.625 / 2.75;
    return 7.5625 * t * t + 0.984375;
  }
}

/**
 * Animate a placed mesh group: children scatter outward then fly in and
 * assemble over 2 seconds with bounce easing + scale from 0 to 1.
 */
function playBuildAnimation(group, onComplete) {
  const children = [];

  group.traverse((child) => {
    if (child.isMesh) {
      children.push({
        mesh: child,
        targetPos: child.position.clone(),
        targetScale: child.scale.clone(),
        startPos: new THREE.Vector3(
          child.position.x + (Math.random() - 0.5) * 4,
          child.position.y + 2 + Math.random() * 3,
          child.position.z + (Math.random() - 0.5) * 4
        ),
      });
      // Set initial scattered state
      child.position.copy(children[children.length - 1].startPos);
      child.scale.set(0, 0, 0);
    }
  });

  // Nothing to animate — finish immediately
  if (children.length === 0) {
    if (onComplete) onComplete();
    return;
  }

  const duration = 2000;
  const startTime = performance.now();

  function animate() {
    const elapsed = performance.now() - startTime;
    const t = Math.min(elapsed / duration, 1);
    const eased = easeOutBounce(t);

    for (const c of children) {
      c.mesh.position.lerpVectors(c.startPos, c.targetPos, eased);
      const s = eased;
      c.mesh.scale.set(
        c.targetScale.x * s,
        c.targetScale.y * s,
        c.targetScale.z * s
      );
    }

    if (t < 1) {
      requestAnimationFrame(animate);
    } else {
      if (onComplete) onComplete();
    }
  }

  requestAnimationFrame(animate);
}

// ---------------------------------------------------------------------------
// Helper: refund resources from a cost map
// ---------------------------------------------------------------------------
function refundResources(costMap) {
  for (const [type, amount] of Object.entries(costMap)) {
    addToInventory(type, amount);
  }
}

// ---------------------------------------------------------------------------
// Public query: is placement mode currently active?
// ---------------------------------------------------------------------------
export function isInPlacementMode() {
  return isPlacementMode;
}

// ---------------------------------------------------------------------------
// SAND CASTLE EROSION — pi-t0j.3
//
// Tracks sand castle meshes and degrades them over 30 real-time minutes:
//   Stage 0 (0-10 min):  Fresh — full opacity, full scale
//   Stage 1 (10-20 min): Weathered — Y scale 0.85, opacity 0.9, brownish tint
//   Stage 2 (20-30 min): Crumbling — Y scale 0.6, opacity 0.7, flattened
//   Stage 3 (30+ min):   Eroded — removed from scene and state
// ---------------------------------------------------------------------------

/** @type {Map<THREE.Group, { placeTime: number, stage: number, id: string }>} */
const sandCastleErosion = new Map();

// Brownish tint for weathered/crumbling stages
const EROSION_TINT = new THREE.Color(0xC4A56C);

/**
 * Register a sand castle mesh for erosion tracking.
 * @param {THREE.Group} mesh - The placed sand castle mesh
 * @param {number} placeTime - Timestamp (Date.now()) when placed
 * @param {string} id - The placed object ID
 */
function trackSandCastleErosion(mesh, placeTime, id) {
  const elapsed = (Date.now() - placeTime) / 60000; // minutes
  let stage;
  if (elapsed < 10) stage = 0;
  else if (elapsed < 20) stage = 1;
  else if (elapsed < 30) stage = 2;
  else stage = 3;

  // If already fully eroded on restore, skip adding the mesh
  if (stage === 3) {
    removeSandCastleFromScene(mesh, id);
    return;
  }

  sandCastleErosion.set(mesh, { placeTime, stage, id });

  // Apply the current stage immediately (for restored objects that have aged)
  if (stage > 0) {
    applyErosionStage(mesh, stage);
  }
}

/**
 * Apply visual erosion effects for a given stage.
 * @param {THREE.Group} mesh
 * @param {number} stage - 0=fresh, 1=weathered, 2=crumbling
 */
function applyErosionStage(mesh, stage) {
  switch (stage) {
    case 1: // Weathered
      mesh.scale.set(1, 0.85, 1);
      mesh.traverse((child) => {
        if (child.isMesh && child.material) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          for (const mat of mats) {
            mat.transparent = true;
            mat.opacity = 0.9;
            // Store original color for tinting
            if (!mat.userData) mat.userData = {};
            if (!mat.userData._originalColor) {
              mat.userData._originalColor = mat.color.clone();
            }
            mat.color.copy(mat.userData._originalColor).lerp(EROSION_TINT, 0.2);
          }
        }
      });
      break;

    case 2: // Crumbling
      mesh.scale.set(1, 0.6, 1);
      mesh.traverse((child) => {
        if (child.isMesh && child.material) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          for (const mat of mats) {
            mat.transparent = true;
            mat.opacity = 0.7;
            if (!mat.userData) mat.userData = {};
            if (!mat.userData._originalColor) {
              mat.userData._originalColor = mat.color.clone();
            }
            mat.color.copy(mat.userData._originalColor).lerp(EROSION_TINT, 0.4);
          }
        }
      });
      break;
  }
}

/**
 * Remove a sand castle from the scene and game state.
 * @param {THREE.Group} mesh
 * @param {string} id - The placed object ID
 */
function removeSandCastleFromScene(mesh, id) {
  // Remove mesh from scene
  if (scene && mesh.parent) {
    scene.remove(mesh);
    mesh.traverse((child) => {
      if (child.isMesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });
  }

  // Remove from erosion tracking
  sandCastleErosion.delete(mesh);

  // Remove from game state
  const state = getGameState();
  if (state.placedObjects) {
    state.placedObjects = state.placedObjects.filter((obj) => obj.id !== id);
    saveGameState(state);
  }

  // Emit removal event
  eventBus.emit(GameEvents.OBJECT_REMOVED, { id, recipeId: 'sand_castle', reason: 'eroded' });

  console.log(`[Placement] Sand castle ${id} has fully eroded and been removed`);
}

/**
 * Update erosion for all tracked sand castles.
 * Call from the main render loop (throttled internally — checks once per second).
 */
let _lastErosionCheck = 0;

export function updateSandCastleErosion() {
  const now = Date.now();

  // Throttle to once per second to avoid per-frame overhead
  if (now - _lastErosionCheck < 1000) return;
  _lastErosionCheck = now;

  for (const [mesh, data] of sandCastleErosion) {
    const elapsed = (now - data.placeTime) / 60000; // minutes

    let newStage;
    if (elapsed < 10) newStage = 0;
    else if (elapsed < 20) newStage = 1;
    else if (elapsed < 30) newStage = 2;
    else newStage = 3;

    if (newStage !== data.stage) {
      data.stage = newStage;

      if (newStage === 3) {
        removeSandCastleFromScene(mesh, data.id);
      } else {
        applyErosionStage(mesh, newStage);
        console.log(`[Placement] Sand castle ${data.id} erosion stage: ${newStage}`);
      }
    }
  }
}
